/**
 * 断点 Design Token 系统
 * 定义完整的响应式断点规范
 */

import type { BreakpointTokens } from './types';

// 断点 Token 定义
export const breakpointTokens: BreakpointTokens = {
  // 移动端断点
  xs: 480,        // 480px - 小屏手机
  sm: 640,        // 640px - 大屏手机
  
  // 平板端断点
  md: 768,        // 768px - 小平板
  lg: 1024,       // 1024px - 大平板/小笔记本
  
  // 桌面端断点
  xl: 1280,       // 1280px - 桌面显示器
  '2xl': 1536,    // 1536px - 大屏显示器
  
  // 超宽屏断点
  '3xl': 1920,    // 1920px - 全高清显示器
  '4xl': 2560,    // 2560px - 2K/4K 显示器
};

// 断点使用规范
export const breakpointUsage = {
  xs: '小屏手机 (iPhone SE, 小安卓机)',
  sm: '大屏手机 (iPhone 12/13/14, 大安卓机)',
  md: '小平板 (iPad mini, 小平板)',
  lg: '大平板/小笔记本 (iPad, Surface, 小笔记本)',
  xl: '桌面显示器 (标准显示器, 笔记本)',
  '2xl': '大屏显示器 (大显示器, 宽屏笔记本)',
  '3xl': '全高清显示器 (1080p 显示器)',
  '4xl': '高分辨率显示器 (2K/4K 显示器)',
};

// 媒体查询生成器
export const mediaQueries = {
  // 最小宽度查询 (移动优先)
  up: (breakpoint: keyof BreakpointTokens) => 
    `@media (min-width: ${breakpointTokens[breakpoint]}px)`,
  
  // 最大宽度查询
  down: (breakpoint: keyof BreakpointTokens) => 
    `@media (max-width: ${breakpointTokens[breakpoint] - 1}px)`,
  
  // 范围查询
  between: (min: keyof BreakpointTokens, max: keyof BreakpointTokens) => 
    `@media (min-width: ${breakpointTokens[min]}px) and (max-width: ${breakpointTokens[max] - 1}px)`,
  
  // 仅特定断点
  only: (breakpoint: keyof BreakpointTokens) => {
    const breakpoints = Object.keys(breakpointTokens) as (keyof BreakpointTokens)[];
    const index = breakpoints.indexOf(breakpoint);
    const nextBreakpoint = breakpoints[index + 1];
    
    if (nextBreakpoint) {
      return `@media (min-width: ${breakpointTokens[breakpoint]}px) and (max-width: ${breakpointTokens[nextBreakpoint] - 1}px)`;
    } else {
      return `@media (min-width: ${breakpointTokens[breakpoint]}px)`;
    }
  },
};

// 容器最大宽度配置
export const containerMaxWidths = {
  xs: '100%',         // 小屏手机 - 全宽
  sm: '100%',         // 大屏手机 - 全宽
  md: '728px',        // 小平板 - 固定宽度
  lg: '984px',        // 大平板 - 固定宽度
  xl: '1240px',       // 桌面 - 固定宽度
  '2xl': '1496px',    // 大屏 - 固定宽度
  '3xl': '1880px',    // 全高清 - 固定宽度
  '4xl': '2520px',    // 高分辨率 - 固定宽度
};

// 网格系统配置
export const gridSystem = {
  // 列数配置
  columns: {
    xs: 4,          // 小屏手机 - 4列
    sm: 6,          // 大屏手机 - 6列
    md: 8,          // 小平板 - 8列
    lg: 12,         // 大平板及以上 - 12列
    xl: 12,         // 桌面 - 12列
    '2xl': 12,      // 大屏 - 12列
    '3xl': 12,      // 全高清 - 12列
    '4xl': 12,      // 高分辨率 - 12列
  },
  
  // 间距配置
  gutters: {
    xs: 16,         // 小屏手机 - 16px 间距
    sm: 16,         // 大屏手机 - 16px 间距
    md: 24,         // 小平板 - 24px 间距
    lg: 24,         // 大平板 - 24px 间距
    xl: 32,         // 桌面 - 32px 间距
    '2xl': 32,      // 大屏 - 32px 间距
    '3xl': 40,      // 全高清 - 40px 间距
    '4xl': 48,      // 高分辨率 - 48px 间距
  },
  
  // 边距配置
  margins: {
    xs: 16,         // 小屏手机 - 16px 边距
    sm: 16,         // 大屏手机 - 16px 边距
    md: 24,         // 小平板 - 24px 边距
    lg: 32,         // 大平板 - 32px 边距
    xl: 40,         // 桌面 - 40px 边距
    '2xl': 48,      // 大屏 - 48px 边距
    '3xl': 56,      // 全高清 - 56px 边距
    '4xl': 64,      // 高分辨率 - 64px 边距
  },
};

// 字体大小响应式配置
export const responsiveFontSizes = {
  xs: {
    h1: 24,         // 小屏手机 H1
    h2: 20,         // 小屏手机 H2
    h3: 18,         // 小屏手机 H3
    h4: 16,         // 小屏手机 H4
    body: 14,       // 小屏手机 正文
    caption: 12,    // 小屏手机 说明
  },
  sm: {
    h1: 28,         // 大屏手机 H1
    h2: 24,         // 大屏手机 H2
    h3: 20,         // 大屏手机 H3
    h4: 18,         // 大屏手机 H4
    body: 16,       // 大屏手机 正文
    caption: 14,    // 大屏手机 说明
  },
  md: {
    h1: 32,         // 小平板 H1
    h2: 28,         // 小平板 H2
    h3: 24,         // 小平板 H3
    h4: 20,         // 小平板 H4
    body: 16,       // 小平板 正文
    caption: 14,    // 小平板 说明
  },
  lg: {
    h1: 36,         // 大平板 H1
    h2: 32,         // 大平板 H2
    h3: 28,         // 大平板 H3
    h4: 24,         // 大平板 H4
    body: 16,       // 大平板 正文
    caption: 14,    // 大平板 说明
  },
  xl: {
    h1: 48,         // 桌面 H1
    h2: 36,         // 桌面 H2
    h3: 30,         // 桌面 H3
    h4: 24,         // 桌面 H4
    body: 16,       // 桌面 正文
    caption: 14,    // 桌面 说明
  },
  '2xl': {
    h1: 56,         // 大屏 H1
    h2: 42,         // 大屏 H2
    h3: 36,         // 大屏 H3
    h4: 28,         // 大屏 H4
    body: 18,       // 大屏 正文
    caption: 16,    // 大屏 说明
  },
};

// 组件响应式配置
export const responsiveComponents = {
  // 按钮尺寸
  button: {
    height: {
      xs: 32,         // 小屏手机按钮高度
      sm: 36,         // 大屏手机按钮高度
      md: 40,         // 平板按钮高度
      lg: 40,         // 大平板按钮高度
      xl: 44,         // 桌面按钮高度
      '2xl': 48,      // 大屏按钮高度
    },
    padding: {
      xs: 12,         // 小屏手机按钮内边距
      sm: 16,         // 大屏手机按钮内边距
      md: 20,         // 平板按钮内边距
      lg: 24,         // 大平板按钮内边距
      xl: 24,         // 桌面按钮内边距
      '2xl': 28,      // 大屏按钮内边距
    },
  },
  
  // 卡片间距
  card: {
    padding: {
      xs: 16,         // 小屏手机卡片内边距
      sm: 16,         // 大屏手机卡片内边距
      md: 20,         // 平板卡片内边距
      lg: 24,         // 大平板卡片内边距
      xl: 24,         // 桌面卡片内边距
      '2xl': 32,      // 大屏卡片内边距
    },
    gap: {
      xs: 16,         // 小屏手机卡片间距
      sm: 16,         // 大屏手机卡片间距
      md: 20,         // 平板卡片间距
      lg: 24,         // 大平板卡片间距
      xl: 24,         // 桌面卡片间距
      '2xl': 32,      // 大屏卡片间距
    },
  },
  
  // 导航栏高度
  navbar: {
    height: {
      xs: 56,         // 小屏手机导航栏高度
      sm: 60,         // 大屏手机导航栏高度
      md: 64,         // 平板导航栏高度
      lg: 68,         // 大平板导航栏高度
      xl: 72,         // 桌面导航栏高度
      '2xl': 80,      // 大屏导航栏高度
    },
  },
};

// 导出断点常量，用于 SCSS 变量兼容
export const breakpointConstants = {
  // 断点值
  BREAKPOINT_XS: breakpointTokens.xs,
  BREAKPOINT_SM: breakpointTokens.sm,
  BREAKPOINT_MD: breakpointTokens.md,
  BREAKPOINT_LG: breakpointTokens.lg,
  BREAKPOINT_XL: breakpointTokens.xl,
  BREAKPOINT_2XL: breakpointTokens['2xl'],
  BREAKPOINT_3XL: breakpointTokens['3xl'],
  BREAKPOINT_4XL: breakpointTokens['4xl'],
  
  // 容器最大宽度
  CONTAINER_XS: containerMaxWidths.xs,
  CONTAINER_SM: containerMaxWidths.sm,
  CONTAINER_MD: containerMaxWidths.md,
  CONTAINER_LG: containerMaxWidths.lg,
  CONTAINER_XL: containerMaxWidths.xl,
  CONTAINER_2XL: containerMaxWidths['2xl'],
  CONTAINER_3XL: containerMaxWidths['3xl'],
  CONTAINER_4XL: containerMaxWidths['4xl'],
} as const;